import type { ApiClient } from '../api/client.js';
import { BleSetupError, delay, type ProvisioningInfo, type DeviceBleTransport, type ReceiptStatus } from './transport.js';

export type ProvisioningProgress = 'sending' | 'verifying' | 'waiting_cloud';
/** What the envelope request carries: written credentials, the saved network, or account networks by id (in priority order). */
export type SavedNetworks = { networkId: string } | { networkIds: string[] };
export type OwnedNetwork = { ssid: string; psk: string } | { reuse: true } | SavedNetworks;
const MAX_NETWORKS = 5;

/** How many networks a unit keeps: INFO `maxNets` (protocol v2), one when the field is absent. */
export function deviceNetworkLimit(info: Pick<ProvisioningInfo, 'maxNets'>): number {
  return typeof info.maxNets === 'number' && info.maxNets > 1 ? Math.min(Math.floor(info.maxNets), MAX_NETWORKS) : 1;
}
/** `networkIds` when the unit keeps several networks, `networkId` (the first) when it keeps one. Null without usable ids. */
export function envelopeNetworks(ids: readonly string[], info: Pick<ProvisioningInfo, 'maxNets'>): SavedNetworks | null {
  const clean = [...new Set(ids.filter(id => typeof id === 'string' && id.length > 0))];
  if (!clean.length) return null;
  return deviceNetworkLimit(info) > 1 ? { networkIds: clean.slice(0, deviceNetworkLimit(info)) } : { networkId: clean[0]! };
}
/** A verified receipt proves save; only a newer account-scoped heartbeat proves
 * cloud connectivity. No success is inferred from discovery or a generic 200. */
export async function provisionOwnedDevice(options: {
  api: ApiClient; transport: Pick<DeviceBleTransport, 'send' | 'disconnect'>; info: ProvisioningInfo;
  network: OwnedNetwork; signal: AbortSignal; onProgress: (state: ProvisioningProgress) => void;
  now?: () => number; wait?: (ms: number, signal: AbortSignal) => Promise<void>; cloudTimeoutMs?: number;
}): Promise<{ saved: boolean; cloudConfirmed: boolean; status: ReceiptStatus }> {
  const { api, transport, info, signal, onProgress } = options;
  const now = options.now ?? (() => performance.now());
  const checkActive = () => { if (signal.aborted) throw new BleSetupError('cancelled'); };
  try {
    checkActive(); onProgress('sending');
    const result = await api.createProvisioningEnvelope(info.id, { nonce: info.nonce, ...options.network }, signal);
    if ('psk' in options.network) options.network.psk = '';
    checkActive();
    // BLE setup suspends the device heartbeat. Capture the account's last
    // server timestamp immediately before writing, while that pause still holds.
    const { devices: before } = await api.listDevices(signal);
    checkActive();
    const baseline = before.find(item => item.id === info.id && item.status !== 'revoked');
    if (!baseline) throw new BleSetupError('account_changed');
    const baselineSeenAt = baseline.lastSeenAt === null ? Number.NEGATIVE_INFINITY : Date.parse(baseline.lastSeenAt);
    const receipt = await transport.send(result.envelope, signal);
    checkActive(); onProgress('verifying');
    const verified = await api.verifyProvisioningReceipt(info.id, { nonce: info.nonce, status: receipt.status, mac: receipt.mac }, signal);
    checkActive();
    if (verified.verified !== true || verified.status !== receipt.status) throw new BleSetupError('invalid_receipt');
    transport.disconnect();
    if (verified.status !== 'saved') return { saved: false, cloudConfirmed: false, status: verified.status };
    onProgress('waiting_cloud');
    const until = now() + (options.cloudTimeoutMs ?? 90_000);
    while (now() <= until) {
      checkActive();
      const { devices } = await api.listDevices(signal);
      checkActive();
      const device = devices.find(item => item.id === info.id && item.status !== 'revoked');
      if (device?.lastSeenAt && Date.parse(device.lastSeenAt) > baselineSeenAt) return { saved: true, cloudConfirmed: true, status: 'saved' };
      if (now() >= until) break;
      await (options.wait ?? delay)(3000, signal);
    }
    return { saved: true, cloudConfirmed: false, status: 'saved' };
  } finally { if ('psk' in options.network) options.network.psk = ''; }
}
